import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { SHEET_TOKENS, getGlassSurface, getTextColors, getBackdropStyle, getInnerCardSurface } from '../pages/MojeSmena/sheetStyle';
import useBottomSheetSwipe from '../hooks/useBottomSheetSwipe';
import useJeMobil from '../hooks/useJeMobil';

/**
 * Standardní modál mobilní aplikace — skleněný sheet, který vyjede zespodu
 * (vzor: „Tým na směně" v Moje směna).
 *
 * Hlavička: ikona v barevné dlaždici, nadpis, podnadpis, křížek. Zavírá se
 * křížkem, klepnutím mimo, Esc nebo (na mobilu) stažením hlavičky dolů.
 * `uvod` stojí pod hlavičkou (např. hlavní tlačítko), `children` scrollují,
 * `paticka` stojí dole. `zavritLze={false}` drží sheet otevřený (probíhající akce).
 *
 * **Na mobilu zdola, na desktopu na střed** — stejné pravidlo jako `ModalShell`,
 * kde je i jeho odůvodnění. Plátno u dolní hrany je rozvržení pro palec;
 * u myši je to jen kartička zaražená do spodní hrany okna. Na desktopu proto
 * modal stojí uprostřed, má zaoblené všechny rohy a stažení dolů se vypíná
 * (úchyt bez dotyku nic neznamená).
 */
export default function SheetModal({
    onClose,
    isDark,
    ikona: Ikona = null,
    barva = '#0CC0DF',
    nadpis,
    podnadpis = null,
    uvod = null,
    children,
    paticka = null,
    zavritLze = true,
    maxSirka = 520,
    minVyska = null,
    zIndex = 1020,
}) {
    const jeMobil = useJeMobil();
    const zavri = () => { if (zavritLze) onClose?.(); };
    const { dragHandleProps, modalStyle } = useBottomSheetSwipe(zavri);

    useEffect(() => {
        const predtim = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = predtim; };
    }, []);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') zavri(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    });

    const sklo = getGlassSurface(isDark);
    const barvy = getTextColors(isDark);

    // Portál do body — sheet musí uniknout stacking contextu rodiče (karty
    // s backdrop-filter / transformem by ho uvěznily pod hlavičkou aplikace).
    return createPortal((
        <div role="dialog" aria-modal="true" aria-label={typeof nadpis === 'string' ? nadpis : undefined}
            onClick={zavri}
            style={{
                ...getBackdropStyle(), zIndex, flexDirection: 'row',
                alignItems: jeMobil ? 'flex-end' : 'center', justifyContent: 'center',
                padding: jeMobil ? 0 : 16,
            }}>
            <div onClick={e => e.stopPropagation()} className="ms-sheet-anim" style={{
                width: jeMobil ? '100%' : `min(${typeof maxSirka === 'number' ? `${maxSirka}px` : maxSirka}, 100%)`,
                maxWidth: `min(${typeof maxSirka === 'number' ? `${maxSirka}px` : maxSirka}, 100%)`,
                // dvh, ne vh — na mobilu se lišta prohlížeče schovává a `vh`
                // by modal přetáhlo pod spodní hranu výřezu
                maxHeight: jeMobil ? '88dvh' : '85vh',
                ...(minVyska ? { minHeight: minVyska } : null),
                boxSizing: 'border-box',
                borderRadius: jeMobil
                    ? `${SHEET_TOKENS.radius.container}px ${SHEET_TOKENS.radius.container}px 0 0`
                    : SHEET_TOKENS.radius.containerDesktop,
                ...sklo,
                padding: jeMobil
                    ? '16px 16px max(16px, env(safe-area-inset-bottom, 0px))'
                    : 16,
                display: 'flex', flexDirection: 'column', gap: 12,
                overflow: 'hidden',
                // Stažení dolů má smysl jen u plátna pod palcem.
                ...(jeMobil ? modalStyle : null),
            }}>
                {/* Hlavička — na mobilu zároveň úchyt pro stažení dolů */}
                <div {...(jeMobil ? dragHandleProps : {})} style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    {Ikona && (
                        <span style={{
                            width: 32, height: 32, borderRadius: 7, flexShrink: 0,
                            background: `${barva}2E`, color: barva,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Ikona size={16} />
                        </span>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontSize: 15, fontWeight: 700, color: barvy.textPrimary, lineHeight: 1.2,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{nadpis}</div>
                        {podnadpis && (
                            <div style={{
                                fontSize: 11.5, color: barvy.textMuted, marginTop: 1,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>{podnadpis}</div>
                        )}
                    </div>
                    {zavritLze && (
                        <button type="button" onClick={zavri} aria-label="Zavřít" style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: barvy.textMuted, padding: 4, lineHeight: 0, flexShrink: 0,
                        }}>
                            <X size={20} />
                        </button>
                    )}
                </div>

                {uvod && <div style={{ flexShrink: 0 }}>{uvod}</div>}

                {/* `ms-sheet-telo` zakazuje dětem smrštění — bez toho se obsah
                    ve sloupcovém flexu zmáčkne místo scrollování (viz app.css). */}
                <div className="ms-sheet-telo" style={{
                    flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
                    overscrollBehavior: 'contain',
                    display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                    {children}
                </div>

                {paticka && <div style={{ flexShrink: 0 }}>{paticka}</div>}
            </div>
        </div>
    ), document.body);
}

/** Karta uvnitř sheetu — stejná jako řádky v „Tým na směně". */
export function SheetKarta({ isDark, akcent = null, style = {}, children, ...props }) {
    const vnitrni = getInnerCardSurface(isDark);
    return (
        <div {...props} style={{
            ...vnitrni,
            borderRadius: SHEET_TOKENS.radius.card, padding: 10,
            ...(akcent ? { borderLeft: `3px solid ${akcent}` } : null),
            ...style,
        }}>
            {children}
        </div>
    );
}

/** Nadpis skupiny v sheetu (malé kapitálky). */
export function SheetNadpisSkupiny({ isDark, children, style = {} }) {
    return (
        <div style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
            color: getTextColors(isDark).textMuted, margin: '6px 2px 0', ...style,
        }}>{children}</div>
    );
}

/** Tlačítko přes celou šířku (patička sheetu). `hlavni` = oranžové CTA. */
export function SheetTlacitko({ isDark, hlavni = false, barva = '#FF8A00', as: Prvek = 'button', style = {}, children, ...props }) {
    const barvy = getTextColors(isDark);
    // `disabled` se přes `...props` dostane na <button>, ale bez vizuálního
    // stavu vypadá tlačítko aktivně a nereaguje — což se čte jako rozbitá
    // aplikace, ne jako „ještě něco chybí".
    const vypnuto = !!props.disabled;
    return (
        <Prvek {...(Prvek === 'button' ? { type: 'button' } : null)} {...props} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', minHeight: 46, padding: '0 14px', boxSizing: 'border-box',
            borderRadius: SHEET_TOKENS.radius.button,
            cursor: vypnuto ? 'not-allowed' : 'pointer',
            opacity: vypnuto ? 0.45 : 1,
            textDecoration: 'none',
            fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
            ...(hlavni ? {
                background: barva === '#FF8A00'
                    ? 'linear-gradient(135deg, #FF8A00 0%, #F59E0B 100%)'
                    : `linear-gradient(135deg, ${barva} 0%, ${barva}D9 100%)`,
                // Na oranžové tmavý text (jako „Napsat všem"), na ostatních bílý
                color: barva === '#FF8A00' ? '#1a1a1f' : '#fff',
                fontWeight: 800, border: 'none', boxShadow: `0 6px 18px ${barva}52`,
            } : {
                ...getInnerCardSurface(isDark), color: barvy.textPrimary,
            }),
            ...style,
        }}>
            {children}
        </Prvek>
    );
}
